import React from 'react';
import { usePreferencesStore } from '../../store/preferencesStore';
import '../styles/NotificationSettings.css';

export interface NotificationSettingsProps {
  className?: string;
}

export const NotificationSettings: React.FC<NotificationSettingsProps> = ({
  className = '',
}) => {
  const { notificationPreferences, setNotificationPreference } = usePreferencesStore();

  const handleToggle = (key: string, value: boolean) => {
    setNotificationPreference(key as any, value);
  };

  const handleMasterToggle = (enabled: boolean) => {
    setNotificationPreference('enabled' as any, enabled);
  };

  return (
    <div className={`notification-settings ${className}`}>
      <div className="notification-settings-header">
        <h2 className="notification-settings-title">Notification Settings</h2>
        <p className="notification-settings-description">
          Manage how you receive notifications about your NFTs and offers
        </p>
      </div>

      {/* Master Toggle */}
      <div className="notification-setting-group">
        <div className="notification-setting-item notification-setting-master">
          <div className="notification-setting-info">
            <h3 className="notification-setting-label">Enable Notifications</h3>
            <p className="notification-setting-help">
              Turn all notifications on or off
            </p>
          </div>
          <label className="notification-toggle">
            <input
              type="checkbox"
              checked={notificationPreferences.enabled}
              onChange={(e) => handleMasterToggle(e.target.checked)}
            />
            <span className="notification-toggle-slider"></span>
          </label>
        </div>
      </div>

      {/* General Settings */}
      <div className="notification-setting-group">
        <h3 className="notification-setting-group-title">General</h3>

        <div className="notification-setting-item">
          <div className="notification-setting-info">
            <h4 className="notification-setting-label">Sound</h4>
            <p className="notification-setting-help">Play sound for notifications</p>
          </div>
          <label className="notification-toggle">
            <input
              type="checkbox"
              checked={notificationPreferences.sound}
              onChange={(e) => handleToggle('sound', e.target.checked)}
              disabled={!notificationPreferences.enabled}
            />
            <span className="notification-toggle-slider"></span>
          </label>
        </div>

        <div className="notification-setting-item">
          <div className="notification-setting-info">
            <h4 className="notification-setting-label">Desktop Notifications</h4>
            <p className="notification-setting-help">
              Show browser desktop notifications
            </p>
          </div>
          <label className="notification-toggle">
            <input
              type="checkbox"
              checked={notificationPreferences.desktop}
              onChange={(e) => handleToggle('desktop', e.target.checked)}
              disabled={!notificationPreferences.enabled}
            />
            <span className="notification-toggle-slider"></span>
          </label>
        </div>
      </div>

      {/* Offer Notifications */}
      <div className="notification-setting-group">
        <h3 className="notification-setting-group-title">Offer Activity</h3>

        <div className="notification-setting-item">
          <div className="notification-setting-info">
            <h4 className="notification-setting-label">Offer Created</h4>
            <p className="notification-setting-help">When you create a new offer</p>
          </div>
          <label className="notification-toggle">
            <input
              type="checkbox"
              checked={notificationPreferences.offerCreated}
              onChange={(e) => handleToggle('offerCreated', e.target.checked)}
              disabled={!notificationPreferences.enabled}
            />
            <span className="notification-toggle-slider"></span>
          </label>
        </div>

        <div className="notification-setting-item">
          <div className="notification-setting-info">
            <h4 className="notification-setting-label">Offer Accepted</h4>
            <p className="notification-setting-help">When your offer is accepted</p>
          </div>
          <label className="notification-toggle">
            <input
              type="checkbox"
              checked={notificationPreferences.offerAccepted}
              onChange={(e) => handleToggle('offerAccepted', e.target.checked)}
              disabled={!notificationPreferences.enabled}
            />
            <span className="notification-toggle-slider"></span>
          </label>
        </div>

        <div className="notification-setting-item">
          <div className="notification-setting-info">
            <h4 className="notification-setting-label">Offer Cancelled</h4>
            <p className="notification-setting-help">When an offer is cancelled</p>
          </div>
          <label className="notification-toggle">
            <input
              type="checkbox"
              checked={notificationPreferences.offerCancelled}
              onChange={(e) => handleToggle('offerCancelled', e.target.checked)}
              disabled={!notificationPreferences.enabled}
            />
            <span className="notification-toggle-slider"></span>
          </label>
        </div>

        <div className="notification-setting-item">
          <div className="notification-setting-info">
            <h4 className="notification-setting-label">Offer Received</h4>
            <p className="notification-setting-help">When you receive a new offer</p>
          </div>
          <label className="notification-toggle">
            <input
              type="checkbox"
              checked={notificationPreferences.offerReceived}
              onChange={(e) => handleToggle('offerReceived', e.target.checked)}
              disabled={!notificationPreferences.enabled}
            />
            <span className="notification-toggle-slider"></span>
          </label>
        </div>
      </div>

      {/* Transaction Notifications */}
      <div className="notification-setting-group">
        <h3 className="notification-setting-group-title">Transactions</h3>

        <div className="notification-setting-item">
          <div className="notification-setting-info">
            <h4 className="notification-setting-label">Transaction Success</h4>
            <p className="notification-setting-help">When a transaction completes</p>
          </div>
          <label className="notification-toggle">
            <input
              type="checkbox"
              checked={notificationPreferences.transactionSuccess}
              onChange={(e) => handleToggle('transactionSuccess', e.target.checked)}
              disabled={!notificationPreferences.enabled}
            />
            <span className="notification-toggle-slider"></span>
          </label>
        </div>

        <div className="notification-setting-item">
          <div className="notification-setting-info">
            <h4 className="notification-setting-label">Transaction Failed</h4>
            <p className="notification-setting-help">When a transaction fails</p>
          </div>
          <label className="notification-toggle">
            <input
              type="checkbox"
              checked={notificationPreferences.transactionFailed}
              onChange={(e) => handleToggle('transactionFailed', e.target.checked)}
              disabled={!notificationPreferences.enabled}
            />
            <span className="notification-toggle-slider"></span>
          </label>
        </div>
      </div>

      {/* Price Alerts */}
      <div className="notification-setting-group">
        <h3 className="notification-setting-group-title">Price Alerts</h3>

        <div className="notification-setting-item">
          <div className="notification-setting-info">
            <h4 className="notification-setting-label">Floor Price Changes</h4>
            <p className="notification-setting-help">
              When floor prices drop for your watched collections
            </p>
          </div>
          <label className="notification-toggle">
            <input
              type="checkbox"
              checked={notificationPreferences.priceAlerts}
              onChange={(e) => handleToggle('priceAlerts', e.target.checked)}
              disabled={!notificationPreferences.enabled}
            />
            <span className="notification-toggle-slider"></span>
          </label>
        </div>
      </div>
    </div>
  );
};
